/**
 * Direct-to-S3 upload helpers for project media (queue bucket).
 * Supports single POST for smaller files and multipart for large (~10GB) uploads.
 */

export const MAX_FILE_SIZE = 10737418240; // 10GB

const MAX_PUT_RETRIES = 3;
const MAX_CONCURRENT_PARTS = 3;
const XHR_TIMEOUT_MS = 5 * 60 * 1000;

export function detectMediaKind(file) {
  if (!file) return null;
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('image/')) return 'image';

  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (['mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm', 'wmv'].includes(ext)) {
    return 'video';
  }
  if (['mp3', 'wav', 'aac', 'flac', 'm4a', 'ogg', 'wma'].includes(ext)) {
    return 'audio';
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
    return 'image';
  }
  return 'other';
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function apiPost(client, path, data) {
  const response = await client.post(path, { data });
  // respondSuccess may return the payload at the root or nested under data
  if (
    response &&
    typeof response === 'object' &&
    response.data &&
    !response.method &&
    !response.key
  ) {
    return response.data;
  }
  return response;
}

/**
 * Upload a File to the queue bucket via signed API endpoints.
 *
 * @param {object} client - ApiClient instance from redux middleware
 * @param {File} file
 * @param {function} onProgress - (percent: number) => void
 * @returns {Promise<{ s3_url: string, key: string }>}
 */
export async function uploadProjectMediaToS3(
  client,
  file,
  onProgress = () => {}
) {
  if (!file) {
    throw new Error('No file selected');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File must be 10GB or smaller');
  }

  const signed = await apiPost(client, '/projects/upload/sign', {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
  });

  if (signed.method === 'POST') {
    await postWithProgress(signed.url, signed.fields || {}, file, onProgress);
    onProgress(100);
    return { s3_url: signed.s3_url, key: signed.key };
  }

  // Legacy PUT support if an older API response is still returned
  if (signed.method === 'PUT') {
    await putWithRetry(signed.url, file, signed.headers || {}, onProgress);
    onProgress(100);
    return { s3_url: signed.s3_url, key: signed.key };
  }

  return uploadMultipart(client, file, signed, onProgress);
}

/**
 * Discard a completed queue-bucket upload owned by the authenticated user.
 *
 * @param {object} client
 * @param {string} key
 * @returns {Promise<void>}
 */
export async function discardProjectMediaUpload(client, key) {
  if (!key) {
    return;
  }

  await apiPost(client, '/projects/upload/discard', { key });
}

function createCancelledError() {
  const error = new Error('Upload cancelled');
  error.cancelled = true;
  return error;
}

async function putWithProgress(url, body, headers, onProgress, activeXhrs) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    xhr.open('PUT', url);
    xhr.timeout = XHR_TIMEOUT_MS;

    Object.keys(headers).forEach((key) => {
      xhr.setRequestHeader(key, headers[key]);
    });

    if (activeXhrs) {
      activeXhrs.add(xhr);
    }

    const settle = (handler, value) => {
      if (settled) return;
      settled = true;
      if (activeXhrs) {
        activeXhrs.delete(xhr);
      }
      handler(value);
    };

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        settle(resolve, xhr);
      } else {
        settle(reject, new Error(`Upload failed (${xhr.status})`));
      }
    };

    xhr.onerror = () => settle(reject, new Error('Upload failed'));
    xhr.ontimeout = () => settle(reject, new Error('Upload timed out'));
    xhr.onabort = () => settle(reject, createCancelledError());
    xhr.send(body);
  });
}

async function postWithProgress(url, fields, file, onProgress) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    // S3 requires the file field to be last
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = XHR_TIMEOUT_MS;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.send(formData);
  });
}

async function putWithRetry(
  url,
  body,
  headers,
  onProgress,
  { activeXhrs, shouldAbort, onFailure } = {}
) {
  let lastError;

  for (let attempt = 0; attempt < MAX_PUT_RETRIES; attempt += 1) {
    if (shouldAbort && shouldAbort()) {
      throw createCancelledError();
    }

    try {
      return await putWithProgress(url, body, headers, onProgress, activeXhrs);
    } catch (error) {
      lastError = error;
      if (error.cancelled) {
        throw error;
      }
      if (onFailure) {
        onFailure(error);
      }
      if (shouldAbort && shouldAbort()) {
        throw error;
      }
      if (attempt < MAX_PUT_RETRIES - 1) {
        await sleep(Math.min(1000 * 2 ** attempt, 8000));
        if (shouldAbort && shouldAbort()) {
          throw createCancelledError();
        }
      }
    }
  }

  throw lastError;
}

async function uploadMultipart(client, file, signed, onProgress) {
  const partSize = signed.part_size || 10485760;
  const totalParts = Math.ceil(file.size / partSize);
  const parts = new Array(totalParts);
  const partProgress = new Array(totalParts).fill(0);
  const activeXhrs = new Set();
  let nextPart = 1;
  let uploadError = null;

  const reportOverall = () => {
    const loaded = partProgress.reduce((sum, value) => sum + value, 0);
    onProgress(Math.min(Math.round((loaded / file.size) * 100), 99));
  };

  const failUpload = (error) => {
    if (uploadError) return;
    uploadError = error;
    activeXhrs.forEach((xhr) => xhr.abort());
  };

  const uploadPart = async (partNumber) => {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    const partSign = await apiPost(client, '/projects/upload/sign-part', {
      key: signed.key,
      upload_id: signed.upload_id,
      part_number: partNumber,
    });

    if (uploadError) {
      throw createCancelledError();
    }

    const response = await putWithRetry(
      partSign.url,
      blob,
      {},
      (partPercent) => {
        partProgress[partNumber - 1] = Math.round(
          (blob.size * partPercent) / 100
        );
        reportOverall();
      },
      {
        activeXhrs,
        shouldAbort: () => Boolean(uploadError),
        onFailure: failUpload,
      }
    );

    const etag = response.getResponseHeader('ETag');
    parts[partNumber - 1] = {
      PartNumber: partNumber,
      ETag: etag,
    };
    partProgress[partNumber - 1] = blob.size;
    reportOverall();
  };

  const workers = Array.from(
    { length: Math.min(MAX_CONCURRENT_PARTS, totalParts) },
    async () => {
      while (nextPart <= totalParts && !uploadError) {
        const partNumber = nextPart;
        nextPart += 1;
        try {
          await uploadPart(partNumber);
        } catch (error) {
          if (error.cancelled) {
            return;
          }
          failUpload(error);
          return;
        }
      }
    }
  );

  // Wait for every worker to finish, including after a rejection, before abort.
  await Promise.all(workers.map((worker) => worker.catch(() => {})));

  if (uploadError) {
    try {
      await apiPost(client, '/projects/upload/abort', {
        key: signed.key,
        upload_id: signed.upload_id,
      });
    } catch (abortError) {
      // best-effort cleanup
    }
    throw uploadError;
  }

  try {
    const completed = await apiPost(client, '/projects/upload/complete', {
      key: signed.key,
      upload_id: signed.upload_id,
      parts,
    });

    onProgress(100);
    return { s3_url: completed.s3_url, key: completed.key };
  } catch (error) {
    try {
      await apiPost(client, '/projects/upload/abort', {
        key: signed.key,
        upload_id: signed.upload_id,
      });
    } catch (abortError) {
      // best-effort cleanup
    }
    throw error;
  }
}
